import { ServerResponse, WithAuditDetails } from 'data/types';
import { AxiosError } from 'axios';
import { History } from 'history';
import { Dispatch, Action } from 'redux';
import { RootState } from 'data/root.reducers';
import i18n from 'i18n';
import { showSuccessMessage, showError } from 'shared/toaster';
import { ThunkDispatch } from 'redux-thunk';
import * as api from './collectionFunctionGroup.api';
import { extractErrorMessage } from '../../utils';
import {
  GET_COLLECTION_FUNCTION_GROUP_LIST,
  GET_COLLECTION_FUNCTION_GROUP,
  CREATE_COLLECTION_FUNCTION_GROUP,
  UPDATE_COLLECTION_FUNCTION_GROUP,
  DELETE_COLLECTION_FUNCTION_GROUP,
  CollectionFunctionGroupActionTypes,
  CollectionFunctionGroup,
  GO_TO_COLLECTION_FUNCTION_GROUP_LIST,
  SELECT_COLLECTION_FUNCTION_GROUP,
  SelectCollectionFunctionGroupAction,
  DeleteCollectionFunctionGroupThunkAction,
  UpdateCollectionFunctionGroupThunkAction,
  CreateCollectionFunctionGroupThunkAction,
  OPEN_COLLECTION_FUNCTION_GROUP_CREATE_PAGE,
  CLOSE_COLLECTION_FUNCTION_GROUP_CREATE_PAGE,
  OPEN_COLLECTION_FUNCTION_GROUP_EDIT_PAGE,
  CLOSE_COLLECTION_FUNCTION_GROUP_EDIT_PAGE,
  SET_COLLECTION_FUNCTION_GROUP_LOADING,
  GO_TO_COLLECTION_FUNCTION_GROUP_VIEW,
  SetCollectionFunctionGroupLoadingAction,
} from './collectionFunctionGroup.types';
import { CF_GROUP_ROUTE, SYSTEM, SYSTEM_CONFIGURATION_ROUTE } from '../../constants';
import { getSelectedCollectionFunctionGroup } from './collectionFunctionGroup.selectors';

export function goToCollectionFunctionGroupList(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_COLLECTION_FUNCTION_GROUP_LIST,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${CF_GROUP_ROUTE}`);
  };
}

export function goToCollectionGroupCreate(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: OPEN_COLLECTION_FUNCTION_GROUP_CREATE_PAGE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${CF_GROUP_ROUTE}/create`);
  };
}

export function closeCollectionFunctionGroupCreatePage(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: CLOSE_COLLECTION_FUNCTION_GROUP_CREATE_PAGE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${CF_GROUP_ROUTE}`);
  };
}

export function goToCollectionGroupEdit(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: OPEN_COLLECTION_FUNCTION_GROUP_EDIT_PAGE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${CF_GROUP_ROUTE}/edit`);
  };
}

export function goToCollectionFunctionGroupView(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_COLLECTION_FUNCTION_GROUP_VIEW,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${CF_GROUP_ROUTE}/view`);
  };
}

export function closeCollectionFunctionGroupEditPage(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: CLOSE_COLLECTION_FUNCTION_GROUP_EDIT_PAGE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${SYSTEM}${CF_GROUP_ROUTE}`);
  };
}

export function setCollectionFunctionGroupLoadingAction(isLoading: boolean): SetCollectionFunctionGroupLoadingAction {
  return {
    type: SET_COLLECTION_FUNCTION_GROUP_LOADING,
    payload: { isLoading },
  };
}

export function getCollectionFunctionGroupList(deliveryFunctionId: string): CollectionFunctionGroupActionTypes {
  return {
    type: GET_COLLECTION_FUNCTION_GROUP_LIST,
    payload: api.getCollectionFunctionGroupList(deliveryFunctionId),
  };
}

export function getCollectionFunctionGroup(deliveryFunctionId: string, id: string): CollectionFunctionGroupActionTypes {
  return {
    type: GET_COLLECTION_FUNCTION_GROUP,
    payload: api.getCollectionFunctionGroup(deliveryFunctionId, id),
  };
}

export function createCollectionFunctionGroup(
  deliveryFunctionId: string,
  body: WithAuditDetails<CollectionFunctionGroup>,
  history: History,
): CreateCollectionFunctionGroupThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: CREATE_COLLECTION_FUNCTION_GROUP,
        payload: api.createCollectionFunctionGroup(deliveryFunctionId, body),
      });

      dispatch(getCollectionFunctionGroupList(deliveryFunctionId));
      dispatch(goToCollectionFunctionGroupList(history));
      // TODO: This must be reenable when the LEA page layout get refactored to fit th proposd UI
      // dispatch(goToCollectionFunctionGroupList(history));
      showSuccessMessage(i18n.t('collectionFunctionGroup.create.success', { name: body.data?.name }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('collectionFunctionGroup.create.failed', { name: body.data?.name, error: errorMessage }));
    }
  };
}

export function createCollectionFunctionGroupFromForm(
  deliveryFunctionId: string,
  body: WithAuditDetails<CollectionFunctionGroup>,
): CreateCollectionFunctionGroupThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    dispatch(setCollectionFunctionGroupLoadingAction(true));
    try {
      await dispatch({
        type: CREATE_COLLECTION_FUNCTION_GROUP,
        payload: api.createCollectionFunctionGroup(deliveryFunctionId, body),
      });

      showSuccessMessage(i18n.t('collectionFunctionGroup.create.success', { name: body.data?.name }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('collectionFunctionGroup.create.failed', { name: body.data?.name, error: errorMessage }));

    } finally {
      dispatch(setCollectionFunctionGroupLoadingAction(false));
      dispatch(getCollectionFunctionGroupList(deliveryFunctionId));
    }
  };
}

export function updateCollectionFunctionGroup(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<CollectionFunctionGroup>,
  history: History,
): UpdateCollectionFunctionGroupThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: UPDATE_COLLECTION_FUNCTION_GROUP,
        payload: api.updateCollectionFunctionGroup(deliveryFunctionId, id, body),
      });

      dispatch(getCollectionFunctionGroupList(deliveryFunctionId));
      // dispatch(closeCollectionFunctionGroupEditPage());
      // TODO: This must be reenable when the LEA page layout get refactored to fit th proposd UI
      dispatch(goToCollectionFunctionGroupList(history));
      showSuccessMessage(i18n.t('collectionFunctionGroup.edit.success', { name: body.data?.name }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('collectionFunctionGroup.edit.failed', { name: body.data?.name, error: errorMessage }));
    }
  };
}

export function deleteCollectionFunctionGroup(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<void>,
  history?: History,
): DeleteCollectionFunctionGroupThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedCollectionFunctionGroup] = getSelectedCollectionFunctionGroup(getState());

    try {
      await dispatch({
        type: DELETE_COLLECTION_FUNCTION_GROUP,
        payload: api.deleteCollectionFunctionGroup(deliveryFunctionId, id, body),
      });

      showSuccessMessage(i18n.t('collectionFunctionGroup.delete.success', { name: selectedCollectionFunctionGroup?.name }), 5000);
      if (history != null) {
        dispatch(goToCollectionFunctionGroupList(history));
      }
    } catch (e) {
      showError(i18n.t('collectionFunctionGroup.delete.failed', { name: selectedCollectionFunctionGroup?.name }));

    } finally {
      dispatch(getCollectionFunctionGroupList(deliveryFunctionId));
    }
  };
}

export function selectCollectionFunctionGroup(
  collectionFunction: CollectionFunctionGroup[],
): SelectCollectionFunctionGroupAction {
  return {
    type: SELECT_COLLECTION_FUNCTION_GROUP,
    payload: collectionFunction,
  };
}
